import { RadioField, SelectField, SelectOptionType } from "@/components";
import CheckboxField from "@/components/CheckboxField";
import {
  ACTION,
  CHANNEL,
  DATA_SOURCE_TYPE,
  optionsAction,
  optionsChannel,
  optionsScheduleMode,
  optionsTriggerBy,
} from "@/partial/campaign/constant";
import { CampaignForm } from "@/partial/campaign/types";
import useCampaignStore from "@/store/campaignStore";
import React, { Fragment, useEffect, useState } from "react";
import {
  Control,
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from "react-hook-form";
import {
  Accordion,
  Button,
  Dialog,
  IconAdd,
  IconDelete,
} from "tvs-unified-lib";
import Field from "../field";
import Template from "../template";
import {
  CampaignChannel,
  DeleteButton,
  Divider,
  Flex,
  IconAddStyle,
  MonthAllocationCheckboxStyled,
  MonthAllocationStyled,
  MonthAllocationSwitchStyled,
  Title,
  TitleContainer,
} from "./styles";
import { DialogActions } from "@/partial/template/template-form/styles";
import AdditionalParameterForm from "../additional-parameter";

interface ChannelProps {
  isEdit: boolean;
  indexChannel: number;
  control: Control<CampaignForm>;
  remove: UseFieldArrayRemove;
  setValue: UseFormSetValue<CampaignForm>;
  watch: UseFormWatch<CampaignForm>;
}

export const Channel = ({
  isEdit,
  indexChannel,
  control,
  remove,
  setValue,
  watch,
}: ChannelProps) => {
  const {
    campaignDetail: campaignDetailStore,
    optionsConditionChannel,
    optionsAdditionalParameters,
  } = useCampaignStore();

  // dae2 is enabled or not
  const isDAE2 = import.meta.env.VITE_ENABLED_DAE2 === "true";

  const [selectedTriggerBy, setSelectedTriggerBy] = useState<
    SelectOptionType[]
  >([]);
  const [selectedCondition, setSelectedCondition] = useState<
    SelectOptionType[]
  >([]);
  const [optionsCondition, setOptionsCondition] = useState<SelectOptionType[]>(
    []
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedAction, setSelectedAction] = useState<SelectOptionType[]>([]);

  const [openAdditionalParamPopUp, setOpenAdditionalParamPopUp] =
    useState<boolean>(false);

  const [selectedFields, setSelectedFields] = useState<SelectOptionType[]>([]);
  const [editCampaignFields, setEditCampaignFields] = useState();
  const [currentSelection, setCurrentSelection] =
    useState<SelectOptionType | null>(null);

  const handleAdd = () => {
    if (currentSelection) {
      setSelectedFields((prev) => [...prev, currentSelection]);
      setCurrentSelection(null);
    }
    setOpenAdditionalParamPopUp(false);
  };

  const {
    fields: templateFields,
    append: appendTemplate,
    remove: removeTemplate,
  } = useFieldArray({
    control,
    name: `campaign_channels[${indexChannel}].campaign_data_channels` as `campaign_channels.${number}.campaign_data_channels`,
  });
  const dataSource = useWatch({
    control,
    name: `data_source` as `data_source`,
  });
  const dataFile = useWatch({
    control,
    name: `data_file` as `data_file`,
  });
  const channel = useWatch({
    control,
    name: `campaign_channels[${indexChannel}].channel` as `campaign_channels.${number}.channel`,
    defaultValue: CHANNEL.SMS,
  });
  const previousMonthAllocation = useWatch({
    control,
    name: `campaign_channels[${indexChannel}].previous_month_allocation` as `campaign_channels.${number}.previous_month_allocation`,
  });
  const action = useWatch({
    control,
    name: `campaign_channels[${indexChannel}].action` as `campaign_channels.${number}.action`,
  });

  const removeChannel = () => {
    if (indexChannel === 0) return;
    remove(indexChannel);
  };

  useEffect(() => {
    if (optionsConditionChannel) {
      setOptionsCondition(optionsConditionChannel);
    }
  }, [optionsConditionChannel]);

  useEffect(() => {
    if (campaignDetailStore?.campaignChannels[indexChannel]?.triggerBy) {
      setSelectedTriggerBy(
        optionsTriggerBy.filter(
          (item) =>
            item.value ===
            campaignDetailStore?.campaignChannels?.[indexChannel]?.triggerBy
        )
      );
    }
    if (campaignDetailStore?.campaignChannels[indexChannel]?.condition) {
      setSelectedCondition(
        optionsCondition?.filter(
          (item) =>
            item.value ===
            campaignDetailStore?.campaignChannels?.[indexChannel]?.condition
        )
      );
    }
    if (campaignDetailStore?.campaignChannels[indexChannel]?.action) {
      setSelectedAction(
        optionsAction.filter(
          (item) =>
            item.value ===
            campaignDetailStore?.campaignChannels?.[indexChannel]?.action
        )
      );
    }
  }, [campaignDetailStore, optionsCondition, indexChannel]);

  useEffect(() => {
    if (
      isEdit &&
      campaignDetailStore?.campaignChannels?.[indexChannel]
        ?.additionalParameters?.nodes
    ) {
      const additionalParams =
        campaignDetailStore.campaignChannels[indexChannel].additionalParameters;
      const apiGroups =
        campaignDetailStore.campaignChannels[indexChannel].additionalParameters
          .nodes;

      const apiFields = apiGroups.map((group: any) => {
        const opt = optionsCondition.find(
          (o) => o.value === group.nodes?.[0]?.field
        );
        return {
          value: group.nodes?.[0]?.field,
          label: opt?.label || group.nodes?.[0]?.field,
        };
      });

      setEditCampaignFields(additionalParams);
      setSelectedFields(apiFields);
    }
  }, [isEdit, campaignDetailStore, indexChannel, optionsCondition]);

  const handleTriggerByChange = (value: SelectOptionType[]) => {
    setSelectedTriggerBy(value);
    setSelectedCondition([]);
    setValue(
      `campaign_channels[${indexChannel}].condition` as `campaign_channels.${number}.condition`,
      []
    );
    setValue(
      `campaign_channels[${indexChannel}].action` as `campaign_channels.${number}.action`,
      []
    );
  };

  const handleConditionChange = (value: SelectOptionType[]) => {
    setSelectedCondition(value);
    setValue(
      `campaign_channels[${indexChannel}].action` as `campaign_channels.${number}.action`,
      []
    );
  };

  const handleActionChange = (value: SelectOptionType[]) => {
    setSelectedAction(value);
  };

  if (optionsCondition.length === 0) {
    return null;
  }

  const additionalParametersOption = Object.values(
    optionsAdditionalParameters
  ).flat();
  return (
    <>
      <CampaignChannel>
        <TitleContainer>
          <Title>Channel {indexChannel + 1}</Title>
          <DeleteButton
            isDisable={indexChannel === 0}
            onClick={() => removeChannel()}
          />
        </TitleContainer>
        <RadioField
          control={control}
          name={`campaign_channels[${indexChannel}].channel`}
          radioValues={optionsChannel}
        />
        {/* if not dae2 will display month allocation */}
        {channel === CHANNEL.FIELD && (
          <MonthAllocationStyled $isTrue={!!previousMonthAllocation}>
            <MonthAllocationSwitchStyled>
              <CheckboxField
                variant="switch"
                theme="green"
                name={`campaign_channels[${indexChannel}].previous_month_allocation`}
                control={control}
                disabled={!isDAE2}
              />
              <p>Previous Month Allocation</p>
            </MonthAllocationSwitchStyled>
            {!!previousMonthAllocation && (
              <MonthAllocationCheckboxStyled>
                <CheckboxField
                  width="100%"
                  variant="box"
                  name={`campaign_channels[${indexChannel}].collection_success`}
                  control={control}
                  label="Collection Success"
                  disabled={!isDAE2}
                />
                <CheckboxField
                  width="100%"
                  variant="box"
                  name={`campaign_channels[${indexChannel}].collection_failure`}
                  control={control}
                  label="Collection Failure"
                  disabled={!isDAE2}
                />
              </MonthAllocationCheckboxStyled>
            )}
          </MonthAllocationStyled>
        )}
        <Flex>
          <SelectField
            name={`campaign_channels[${indexChannel}].trigger_by`}
            label="Trigger By"
            placeholder="Select Trigger"
            isRequired={channel !== CHANNEL.FIELD}
            control={control}
            options={optionsTriggerBy}
            onChange={(e) => handleTriggerByChange(e)}
            disabled={channel === CHANNEL.FIELD}
          />
          <SelectField
            name={`campaign_channels[${indexChannel}].condition`}
            label="Condition"
            placeholder="Select Condition"
            isRequired={channel !== CHANNEL.FIELD}
            control={control}
            options={optionsCondition}
            disabled={
              channel === CHANNEL.FIELD ||
              selectedTriggerBy.length === 0 ||
              (dataSource?.[0]?.value === DATA_SOURCE_TYPE.DATA_UPLOAD &&
                !dataFile?.[0]?.other?.headerMapping)
            }
            onChange={(e) => handleConditionChange(e)}
          />
          <SelectField
            name={`campaign_channels[${indexChannel}].action`}
            label="Action"
            placeholder="Select Action"
            isRequired={channel !== CHANNEL.FIELD}
            control={control}
            options={optionsAction}
            disabled={
              selectedCondition.length === 0 || selectedTriggerBy.length === 0
            }
            onChange={(e) => handleActionChange(e)}
          />
        </Flex>
        <Divider />
        {channel === CHANNEL.FIELD ? (
          <Field
            control={control}
            indexChannel={indexChannel}
            watch={watch}
            disabled={!isDAE2}
          />
        ) : (
          <Fragment>
            <RadioField
              control={control}
              name={`campaign_channels[${indexChannel}].is_check_day`}
              radioValues={optionsScheduleMode}
              // update logic disabled when action is on date
              disabled={
                action
                  ? action[0]?.value === ACTION.ON_DATE
                  : campaignDetailStore?.campaignChannels?.[indexChannel]
                      ?.action === ACTION.ON_DATE
              }
            />
            {templateFields.map((field, indexTemplate) => (
              <React.Fragment key={field.id}>
                <Flex>
                  <Template
                    isEdit={isEdit}
                    indexTemplate={indexTemplate}
                    indexChannel={indexChannel}
                    control={control}
                    setValue={setValue}
                  />
                  {templateFields.length < 3 && (
                    <Button
                      iconOnly
                      variant="outline"
                      className="button-add"
                      icon={<IconAddStyle />}
                      onClick={() =>
                        appendTemplate({
                          day: [],
                          time_band: [],
                          template_script_id: [],
                        })
                      }
                    />
                  )}
                  <Button
                    iconOnly
                    variant="outline"
                    className="button-delete"
                    icon={<IconDelete />}
                    disabled={indexTemplate === 0}
                    onClick={() => removeTemplate(indexTemplate)}
                  />
                </Flex>
              </React.Fragment>
            ))}

            {selectedFields.length > 0 && (
              <>
                <Divider />
                <Accordion
                  defaultOpen
                  header="Additional Parameter"
                  className="card-additional-parameter"
                >
                  <AdditionalParameterForm
                    optionsAdditionalParameters={optionsAdditionalParameters}
                    selectedFields={selectedFields}
                    isEdit={isEdit}
                    control={control}
                    editCampaignFields={editCampaignFields}
                    setValue={setValue}
                    indexChannel={indexChannel}
                    onRemoveField={(fieldValue: string) =>
                      setSelectedFields((prev) =>
                        prev.filter((f) => f.value !== fieldValue)
                      )
                    }
                  />
                </Accordion>
              </>
            )}
            <Button
              label="Additional Parameter"
              className="button-add-parameter"
              variant="outline"
              iconPosition="left"
              onClick={() => setOpenAdditionalParamPopUp(true)}
              icon={<IconAdd fill="#3548c9" />}
              disabled={
                !dataFile?.[0]?.other?.user_field_mapping?.mandatoryFields
                  ?.length ||
                dataSource?.[0]?.value !== DATA_SOURCE_TYPE.DATA_UPLOAD ||
                selectedFields.length >= additionalParametersOption.length
              }
            />
          </Fragment>
        )}
      </CampaignChannel>

      {openAdditionalParamPopUp && (
        <Dialog
          title="Additional Parameter"
          size="sm"
          onBackdropClick={() => setOpenAdditionalParamPopUp(false)}
          className="dialog-additional-parameter"
        >
          <RadioField
            radioValues={additionalParametersOption.map((opt) => ({
              ...opt,
              disabled: selectedFields.some((f) => f.value === opt.value), // disable already selected
            }))}
            alignment="vertical"
            value={currentSelection?.value ?? ""}
            onChange={(val) => {
              const selected = additionalParametersOption.find(
                (opt) => opt.value === val
              );
              setCurrentSelection(selected || null);
            }}
          />
          <DialogActions>
            <Button
              label="Cancel"
              variant="outline"
              size="md"
              onClick={() => setOpenAdditionalParamPopUp(false)}
            />
            <Button label="Add" size="md" onClick={handleAdd} />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default Channel;
